import { useLazyQuery } from '@apollo/client/react';
import { useEffect, useMemo } from 'react';

import {
  GetReceptionPaneDraftAppointmentDocument,
  ReceptionPaneDraftAppointmentFragment,
} from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import { usePreviousValue } from '~/hooks/use-previous-value';
import { useSubscriptions } from '~/hooks/use-subscriptions';

type DraftAppointment = {
  loading: boolean;
  draftAppointment?: ReceptionPaneDraftAppointmentFragment;
};

export const useFetchDraftAppointment = (draftAppointmentId: string): DraftAppointment => {
  const [event, clear] = useSubscriptions('DraftAppointment', ['updated']);

  const [getDraftAppointment, { loading, data }] = useLazyQuery(
    GetReceptionPaneDraftAppointmentDocument,
    {
      fetchPolicy: 'network-only',
    },
  );

  const prevData = usePreviousValue(data);
  const currentData = data || prevData;
  const draftAppointment = useMemo(() => {
    const _draftAppointment = getNode(currentData, 'DraftAppointment');

    if (_draftAppointment?.id === draftAppointmentId) {
      return _draftAppointment;
    }
  }, [draftAppointmentId, currentData]);

  useEffect(() => {
    if (draftAppointmentId) {
      getDraftAppointment({ variables: { draftAppointmentId } });
    }
  }, [draftAppointmentId, getDraftAppointment]);

  useEffect(() => {
    if (!data || !event) return;

    clear();
  }, [clear, data, event]);

  useEffect(() => {
    if (event?.actionName === 'updated' && event.typeId === draftAppointmentId) {
      getDraftAppointment({ variables: { draftAppointmentId } });
    }
  }, [draftAppointmentId, event?.actionName, event?.typeId, getDraftAppointment]);

  return { loading, draftAppointment };
};
